import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";
import { useTheme } from "@chakra-ui/react";
import { colors, lightColorWithDarkTheme } from "../constants/GlobalStyles";

/**
 * AuroraProgressBar
 *
 * The "Aurora" progress-bar design handed off from Claude Design. The fill is
 * a luminous green liquid with a slow hue-drift (overlay-blended canvas), dark
 * drifting "ribbon" smoke (multiply-blended canvas so it only ever darkens),
 * a glossy top sheen, and a two-layer "inner + outer" glow (a tight bright core
 * plus a wide diffuse halo) that follows the filled portion.
 */

// Fill — the luminous green liquid: a vertical gradient, top → mid → bottom.
const FILL_GRADIENT = "linear-gradient(180deg, #5FCC85 0%, #3EB96D 55%, #2A8F4F 100%)";
// Hue (HSL degrees) anchoring the in-fill hue-drift canvas.
const HUE_DRIFT_CENTER_DEGREES = 142;
// The four hue-drift patches, biased around HUE_DRIFT_CENTER_DEGREES so they
// read as variations on the base green rather than foreign colours.
const HUE_DRIFT_PATCHES = [
  { hue: HUE_DRIFT_CENTER_DEGREES, saturationPercent: 70, lightnessPercent: 58 },
  { hue: HUE_DRIFT_CENTER_DEGREES + 15, saturationPercent: 60, lightnessPercent: 50 },
  { hue: HUE_DRIFT_CENTER_DEGREES - 50, saturationPercent: 60, lightnessPercent: 55 },
  { hue: HUE_DRIFT_CENTER_DEGREES - 10, saturationPercent: 75, lightnessPercent: 45 },
];

// The unfilled track — locked to the original ProgressMilestoneBar colour.
const TRACK_COLOR = lightColorWithDarkTheme("#DFE9E3", colors.surface.tertiary);
// Pill radius. Far larger than the bar height, so an overflow:hidden fill caps
// its leading (right) edge as a clean half-circle instead of a flat cut.
const PILL_CORNER_RADIUS = 100;
// Glossy surface: bright highlight at the top fading to clear, then a faint
// shadow at the very bottom — gives the fill a 3D, cylindrical-tube look.
const FILL_GLOSS_GRADIENT =
  "linear-gradient(to bottom," +
  " rgba(255,255,255,0.36) 0%," +
  " rgba(255,255,255,0.144) 22%," +
  " rgba(255,255,255,0) 48%," +
  " rgba(0,0,0,0) 70%," +
  " rgba(0,20,8,0.10) 100%)";

// Spring preset (framer-motion). The fill grows AND shrinks at the same gentle
// pace — a low stiffness so the width eases smoothly over a couple of seconds.
const GROWTH_SPRING: Transition = { type: "spring", stiffness: 13, damping: 7, mass: 1 };
const REDUCED_MOTION_WIDTH_TRANSITION: Transition = { duration: 0.25, ease: "easeOut" };

// Inner + outer stack glow — the "Lime · 80°" colour study from the Glow Color
// Studies handoff. A tight bright core hugging the bar plus a wide diffuse halo;
// the chartreuse hue pops against the green-tinted app background instead of
// dissolving into it. Both layers sit behind the opaque track, so only their
// blurred bleed is visible. (The glow colour is intentionally independent of the
// fill colour — the fill stays Leaf green, the halo is lime.)
const GLOW_INNER_COLOR = colors.surface.progressGlowInner;
const GLOW_INNER_BLUR_IN_PIXELS = 6;
const GLOW_INNER_OPACITY = 0.9;
const GLOW_OUTER_COLOR = colors.surface.progressGlowOuter;
const GLOW_OUTER_BLUR_IN_PIXELS = 32;
const GLOW_OUTER_OPACITY = 0.75;

// Brighter fill for the "new progress" delta segment (used in "newProgressDelta"
// glow mode). A lifted lime-green that reads clearly brighter than the base
// FILL_GRADIENT, so the freshly-earned chunk pops against the settled progress.
const NEW_PROGRESS_DELTA_FILL_GRADIENT = colors.surface.progressGain;

// Smoke appearance — values dialed in live via a (since-removed) tuning panel.
// Small, dense, faint, lightly-blurred ribbons that overlap into soft, connected
// smoke rather than reading as separate dark spots.
const SMOKE_OPACITY = 0.12; // per-ribbon centre alpha — the maximum smoke darkness
const SMOKE_SPACING_HEIGHT_FACTOR = 1.1; // ribbon spacing = barHeight * this
const SMOKE_SIZE_HEIGHT_FACTOR = 1.9; // ribbon radius = barHeight * this
const SMOKE_BLUR_IN_PIXELS = 3; // smoke blur — more merges the ribbons together
// Safety cap on ribbon count; the actual count is derived from the bar width.
const MAX_SMOKE_RIBBON_COUNT = 30;

/** Redraws caps at ~30fps — plenty for the slow Aurora textures, and lighter. */
const CANVAS_FRAME_INTERVAL_MS = 1000 / 30;

type CanvasDrawFunction = (
  drawingContext: CanvasRenderingContext2D,
  cssWidthInPixels: number,
  cssHeightInPixels: number,
  elapsedSeconds: number
) => void;

/**
 * Drives an HTMLCanvas with a requestAnimationFrame loop. The canvas backing
 * store is kept in sync with its CSS size at device-pixel-ratio resolution
 * every frame, so it stays crisp even while the fill width is springing. When
 * the user prefers reduced motion the canvas is painted exactly once.
 */
function useAnimatedCanvas(
  canvasElementRef: React.RefObject<HTMLCanvasElement>,
  draw: CanvasDrawFunction,
  prefersReducedMotion: boolean,
  effectDependencies: React.DependencyList
) {
  useEffect(() => {
    const canvasElement = canvasElementRef.current;
    if (!canvasElement) {
      return;
    }
    const drawingContext = canvasElement.getContext("2d");
    if (!drawingContext) {
      return;
    }

    const syncCanvasSizeToCss = () => {
      const boundingRect = canvasElement.getBoundingClientRect();
      const devicePixelRatio = Math.min(2, window.devicePixelRatio || 1);
      const targetBackingWidth = Math.max(1, Math.round(boundingRect.width * devicePixelRatio));
      const targetBackingHeight = Math.max(1, Math.round(boundingRect.height * devicePixelRatio));
      if (canvasElement.width !== targetBackingWidth || canvasElement.height !== targetBackingHeight) {
        canvasElement.width = targetBackingWidth;
        canvasElement.height = targetBackingHeight;
      }
      drawingContext.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      return { cssWidth: boundingRect.width, cssHeight: boundingRect.height };
    };

    if (prefersReducedMotion) {
      const { cssWidth, cssHeight } = syncCanvasSizeToCss();
      if (cssWidth > 0 && cssHeight > 0) {
        draw(drawingContext, cssWidth, cssHeight, 0);
      }
      return;
    }

    let animationFrameId = 0;
    const animationStartTimestamp = performance.now();
    let lastPaintedTimestamp = 0;

    const renderFrame = (currentTimestamp: number) => {
      animationFrameId = requestAnimationFrame(renderFrame);
      if (currentTimestamp - lastPaintedTimestamp < CANVAS_FRAME_INTERVAL_MS) {
        return;
      }
      lastPaintedTimestamp = currentTimestamp;
      const { cssWidth, cssHeight } = syncCanvasSizeToCss();
      if (cssWidth > 0 && cssHeight > 0) {
        draw(drawingContext, cssWidth, cssHeight, (currentTimestamp - animationStartTimestamp) / 1000);
      }
    };

    animationFrameId = requestAnimationFrame(renderFrame);
    return () => cancelAnimationFrame(animationFrameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, effectDependencies);
}

/**
 * The slow hue-drift layer. Four overlapping HSL patches drift horizontally on
 * independent sine phases; overlay-blended over the base gradient so the colour
 * shift stays clipped to the green family (it never washes toward white).
 */
const AuroraHueDriftCanvas = React.memo(function AuroraHueDriftCanvas({
  canvasWidthInPixels,
  prefersReducedMotion,
}: {
  canvasWidthInPixels: number;
  prefersReducedMotion: boolean;
}) {
  const canvasElementRef = useRef<HTMLCanvasElement>(null);

  useAnimatedCanvas(
    canvasElementRef,
    (drawingContext, width, height, elapsedSeconds) => {
      drawingContext.clearRect(0, 0, width, height);
      HUE_DRIFT_PATCHES.forEach((patch, patchIndex) => {
        const homeX = ((patchIndex + 0.5) / HUE_DRIFT_PATCHES.length) * width;
        const centerX = homeX + Math.sin(elapsedSeconds * 0.16 + patchIndex * 1.7) * width * 0.18;
        const centerY = height * 0.5;
        const patchRadius = Math.max(1, width * 0.55);
        const radialGradient = drawingContext.createRadialGradient(
          centerX,
          centerY,
          0,
          centerX,
          centerY,
          patchRadius
        );
        const hslPrefix = `${patch.hue},${patch.saturationPercent}%,${patch.lightnessPercent}%`;
        radialGradient.addColorStop(0, `hsla(${hslPrefix},0.32)`);
        radialGradient.addColorStop(1, `hsla(${hslPrefix},0)`);
        drawingContext.fillStyle = radialGradient;
        drawingContext.fillRect(0, 0, width, height);
      });
    },
    prefersReducedMotion,
    [prefersReducedMotion]
  );

  return (
    <canvas
      ref={canvasElementRef}
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        // Full bar width — the fill's clip reveals it as the bar grows, so the
        // aurora is anchored in place rather than re-laid-out on every frame.
        width: canvasWidthInPixels,
        height: "100%",
        mixBlendMode: "overlay",
        opacity: 0.85,
        pointerEvents: "none",
      }}
    />
  );
});

/**
 * The drifting "ribbon" smoke. Small, dense, faint dark blobs — each squashed
 * flat into a horizontal ribbon and lightly blurred — drift slowly inside the
 * fill and overlap into soft, connected, flowing smoke. multiply blend means
 * the smoke can only ever DARKEN the fill, never lighten it toward white.
 */
const AuroraSmokeCanvas = React.memo(function AuroraSmokeCanvas({
  isLargeBar,
  canvasWidthInPixels,
  prefersReducedMotion,
}: {
  isLargeBar: boolean;
  canvasWidthInPixels: number;
  prefersReducedMotion: boolean;
}) {
  const canvasElementRef = useRef<HTMLCanvasElement>(null);
  const driftSpeed = 0.65;
  // The blur is tuned for the large desktop bar; scale it down for small bars.
  const ribbonBlurInPixels = SMOKE_BLUR_IN_PIXELS * (isLargeBar ? 1 : 0.6);
  const smokeColor = `rgba(0,32,16,${SMOKE_OPACITY})`;
  const smokeColorTransparent = "rgba(0,32,16,0)";

  useAnimatedCanvas(
    canvasElementRef,
    (drawingContext, width, height, elapsedSeconds) => {
      drawingContext.clearRect(0, 0, width, height);
      drawingContext.filter = `blur(${ribbonBlurInPixels}px)`;

      // The canvas spans the FULL bar width, so `width` here is the whole bar.
      // The ribbon count scales with it; the fill's clip then reveals ribbons
      // as the bar grows — the ribbons themselves never move or re-lay-out.
      const ribbonSpacingInPixels = height * SMOKE_SPACING_HEIGHT_FACTOR;
      const ribbonCount = Math.min(
        MAX_SMOKE_RIBBON_COUNT,
        Math.max(1, Math.round(width / ribbonSpacingInPixels))
      );

      for (let ribbonIndex = 0; ribbonIndex < ribbonCount; ribbonIndex++) {
        // Anchored to absolute positions in full-bar space, so each ribbon
        // holds still as the bar grows and reveals more of them.
        const homeX = (ribbonIndex + 0.5) * ribbonSpacingInPixels;
        const centerX = homeX + Math.sin(elapsedSeconds * 0.25 * driftSpeed + ribbonIndex * 1.3) * width * 0.07;
        const centerY =
          height / 2 + Math.sin(elapsedSeconds * 0.18 * driftSpeed + ribbonIndex * 2.0) * height * 0.36;
        const radiusX = Math.max(
          1,
          height * (SMOKE_SIZE_HEIGHT_FACTOR + 0.5 * Math.sin(elapsedSeconds * 0.3 + ribbonIndex))
        );
        const radiusY = height * 0.55;
        const radialGradient = drawingContext.createRadialGradient(
          centerX,
          centerY,
          0,
          centerX,
          centerY,
          radiusX
        );
        radialGradient.addColorStop(0, smokeColor);
        radialGradient.addColorStop(1, smokeColorTransparent);
        // Squash the circular gradient vertically so it reads as a flat ribbon.
        drawingContext.save();
        drawingContext.translate(centerX, centerY);
        drawingContext.scale(1, radiusY / radiusX);
        drawingContext.translate(-centerX, -centerY);
        drawingContext.fillStyle = radialGradient;
        drawingContext.fillRect(0, 0, width, height * 4);
        drawingContext.restore();
      }
      drawingContext.filter = "none";
    },
    prefersReducedMotion,
    [ribbonBlurInPixels, prefersReducedMotion]
  );

  return (
    <canvas
      ref={canvasElementRef}
      aria-hidden
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        // Full bar width — revealed by the fill's clip as the bar grows.
        width: canvasWidthInPixels,
        height: "100%",
        mixBlendMode: "multiply",
        pointerEvents: "none",
      }}
    />
  );
});

/**
 * The inner + outer stack glow. Two stacked, blurred pills filling their
 * (progress-following) parent: a tight bright core that hugs the bar's edge,
 * and a wide diffuse halo. Both sit behind the opaque track, so only their
 * blurred bleed shows — a crisp bright rim around the fill plus a soft aura.
 *
 * It is static (no per-frame animation); the parent wrapper springs its width
 * so the whole glow grows with the fill.
 */
const AuroraInnerOuterGlow = React.memo(function AuroraInnerOuterGlow({
  blurScale,
}: {
  blurScale: number;
}) {
  return (
    <>
      {/* Inner — tight bright core hugging the bar. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: PILL_CORNER_RADIUS,
          background: GLOW_INNER_COLOR,
          filter: `blur(${GLOW_INNER_BLUR_IN_PIXELS * blurScale}px)`,
          opacity: GLOW_INNER_OPACITY,
        }}
      />
      {/* Outer — wide, diffuse halo. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: PILL_CORNER_RADIUS,
          background: GLOW_OUTER_COLOR,
          // The dark theme pulls the wide halo in (--jungle-art-progress-glow-reach in jungleThemeColors.css)
          filter: `blur(calc(${GLOW_OUTER_BLUR_IN_PIXELS * blurScale}px * var(--jungle-art-progress-glow-reach, 1)))`,
          opacity: `calc(${GLOW_OUTER_OPACITY} * var(--jungle-art-progress-glow-strength, 1))`,
        }}
      />
    </>
  );
});

type AuroraProgressBarProps = {
  /** Progress from 0 to 100. */
  progressPercentage: number;
  /** Use when no trustworthy numeric progress signal exists. */
  isIndeterminate?: boolean;
  /**
   * Fill percentage to render BEFORE the first animation — the spring then
   * grows from here to `progressPercentage`. Defaults to 0 (grow from empty).
   * Set this to replay growth from a specific point (e.g. a review checkpoint).
   */
  initialFillPercentage?: number;
  /** Height of the filled track in pixels. */
  trackHeight?: number;
  /** Width of the bar — a pixel number, or any CSS width string. */
  width?: number | string;
  /**
   * Whether to render the animated hue-drift + smoke canvases. Turn off for
   * tiny bars or lists of many bars, where the per-bar canvas loops aren't
   * worth the cost and the texture is imperceptible anyway.
   */
  showAnimatedTextureCanvases?: boolean;
  /**
   * Glow behavior:
   *  - "full" (default): the inner+outer glow spans the entire filled portion.
   *  - "newProgressDelta": NO base glow. Instead, only the newly-added segment
   *    [newProgressDeltaStartPercentage → progressPercentage] is rendered
   *    brighter and glowing. Used on the review bar to emphasize just-earned
   *    progress. With no delta start provided, the bar is plain (no glow).
   */
  glowMode?: "full" | "newProgressDelta";
  /** In "newProgressDelta" mode, the percentage where the emphasized delta begins. */
  newProgressDeltaStartPercentage?: number;
  /**
   * Points along the bar (0 to 100) drawn as a thin white line across it: the
   * marks of a review (review-progress-marks/). A mark the fill has not
   * reached is solid; once the fill passes it, it flashes and fades back, and
   * stays faint. The state follows the fill as drawn, which trails the
   * progress value while it grows.
   */
  markPercentages?: number[];
  /** Extra styles merged onto the outer container. */
  style?: React.CSSProperties;
};

// A mark is white in both themes (the owner's pick on the real review screen,
// 2026-10-05): it has to read on the green fill as well as on the track.
const MARK_COLOR = "#FFFFFF";
const MARK_WIDTH_IN_PIXELS = 3;
const PASSED_MARK_OPACITY = 0.45;
const MARK_FLASH_SECONDS = 0.7;
const MARK_WITHOUT_GLOW = "0 0 0 0 rgba(255, 255, 255, 0)";
const MARK_GLOW = "0 0 10px 5px rgba(255, 255, 255, 0.9)";
const NO_MARK_PERCENTAGES: number[] = [];

// Framer hands the fill's animated width back as it was given: "42.5%".
function getPercentageFromAnimatedWidth(animatedWidth: unknown): number | null {
  const percentage = typeof animatedWidth === "number" ? animatedWidth : parseFloat(String(animatedWidth));
  return Number.isFinite(percentage) ? percentage : null;
}

function countMarksAtOrBelow(markPercentages: number[], fillPercentage: number): number {
  return markPercentages.filter((markPercentage) => fillPercentage >= markPercentage).length;
}

export const AuroraProgressBar = React.memo(function AuroraProgressBar({
  progressPercentage,
  isIndeterminate = false,
  initialFillPercentage = 0,
  trackHeight = 16,
  width = "100%",
  showAnimatedTextureCanvases = true,
  glowMode = "full",
  newProgressDeltaStartPercentage,
  markPercentages = NO_MARK_PERCENTAGES,
  style,
}: AuroraProgressBarProps) {
  const isRightToLeft = useTheme().direction === "rtl";
  // framer-motion types useReducedMotion() as `boolean | null`; collapse the
  // null to a definite boolean so it flows cleanly into the boolean props below.
  const prefersReducedMotion = useReducedMotion() ?? false;

  const clampedProgressPercentage = Math.min(100, Math.max(0, progressPercentage || 0));
  // The pre-animation fill — the spring grows from here to the current value.
  const clampedInitialFillPercentage = Math.min(100, Math.max(0, initialFillPercentage || 0));
  const initialWidthCss = `${clampedInitialFillPercentage}%`;

  // The aurora canvases are drawn at the FULL bar width and revealed by the
  // fill's clip, so the texture stays anchored in place as the bar grows
  // (rather than re-laying-out frame-to-frame). They need the track's measured
  // pixel width for that.
  const trackElementRef = useRef<HTMLDivElement>(null);
  const [trackPixelWidth, setTrackPixelWidth] = useState(0);
  useEffect(() => {
    const trackElement = trackElementRef.current;
    if (!trackElement) {
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      setTrackPixelWidth(entries[0].contentRect.width);
    });
    resizeObserver.observe(trackElement);
    return () => resizeObserver.disconnect();
  }, []);

  const filledWidthCss = clampedProgressPercentage <= 0 ? "0%" : `${clampedProgressPercentage}%`;

  // How far the fill has been DRAWN, for the marks: a mark turns when the fill
  // visibly passes it, not when the progress value jumps. Only stored when it
  // changes which marks are passed, so the growth animation does not re-render
  // the bar on every frame.
  const [drawnFillPercentageForMarks, setDrawnFillPercentageForMarks] = useState(clampedInitialFillPercentage);
  const noteDrawnFillPercentage = (drawnFillPercentage: number | null) => {
    if (markPercentages.length === 0 || drawnFillPercentage === null) return;
    setDrawnFillPercentageForMarks((previousDrawnFillPercentage) =>
      countMarksAtOrBelow(markPercentages, previousDrawnFillPercentage) ===
      countMarksAtOrBelow(markPercentages, drawnFillPercentage)
        ? previousDrawnFillPercentage
        : drawnFillPercentage
    );
  };
  const widthTransition: Transition = prefersReducedMotion ? REDUCED_MOTION_WIDTH_TRANSITION : GROWTH_SPRING;

  // The outer box reserves trackHeight + 10px. The glow is absolutely
  // positioned and blurred, so it bleeds past this box without enlarging the
  // bar's layout footprint.
  const reservedBoxHeight = trackHeight + 10;
  const isLargeBar = trackHeight >= 12;
  // The glow blur radii are tuned for the large bar; scale them down on a
  // small bar so the halo stays in proportion.
  const glowBlurScale = isLargeBar ? 1 : 0.6;

  // "New progress" delta emphasis (only in "newProgressDelta" glow mode): the
  // segment [start → progress] is rendered brighter and glowing. When there's no
  // valid delta (no start, or start >= progress) the bar shows no glow at all.
  const clampedNewProgressDeltaStartPercentage =
    typeof newProgressDeltaStartPercentage === "number"
      ? Math.min(100, Math.max(0, newProgressDeltaStartPercentage))
      : null;
  const shouldEmphasizeNewProgressDelta =
    glowMode === "newProgressDelta" &&
    clampedNewProgressDeltaStartPercentage !== null &&
    clampedNewProgressDeltaStartPercentage < clampedProgressPercentage;
  const newProgressDeltaStartWidthCss = `${clampedNewProgressDeltaStartPercentage ?? 0}%`;
  const newProgressDeltaWidthCss = `${clampedProgressPercentage - (clampedNewProgressDeltaStartPercentage ?? 0)}%`;

  return (
    <div
      role="progressbar"
      aria-valuenow={isIndeterminate ? undefined : Math.round(clampedProgressPercentage)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        position: "relative",
        width,
        height: reservedBoxHeight,
        display: "flex",
        alignItems: "center",
        ...style,
      }}
    >
      {/* Inner box — the bar's true height. Glow / track / fill all sit here. */}
      {/* Only the graphical track mirrors: percentages, labels and the tree's art keep their own direction. */}
      <div style={{ position: "relative", width: "100%", height: trackHeight, transform: isRightToLeft ? "scaleX(-1)" : undefined }}>
        {/* Glow — inner + outer stack. Width follows the fill (same spring), so
            the glow grows with progress and never lights the empty track. It
            sits behind the opaque track; only its blurred bleed is visible.
            "full" mode only — in "newProgressDelta" mode the base bar is
            glowless and only the delta segment (below) glows. */}
        {!isIndeterminate && glowMode === "full" && (
          <motion.div
            aria-hidden
            initial={{ width: initialWidthCss }}
            animate={{ width: filledWidthCss }}
            transition={{ width: widthTransition }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <AuroraInnerOuterGlow blurScale={glowBlurScale} />
          </motion.div>
        )}

        {/* Delta glow — halos ONLY the newly-earned segment [start → progress].
            Positioned at the delta start; its width grows with the fill spring,
            so it lights up in lockstep with the brighter delta fill below. */}
        {!isIndeterminate && shouldEmphasizeNewProgressDelta && (
          <motion.div
            aria-hidden
            initial={{ width: "0%" }}
            animate={{ width: newProgressDeltaWidthCss }}
            transition={{ width: widthTransition }}
            style={{
              position: "absolute",
              top: 0,
              left: newProgressDeltaStartWidthCss,
              height: "100%",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            <AuroraInnerOuterGlow blurScale={glowBlurScale} />
          </motion.div>
        )}

        {/* Track — the unfilled background AND a stable rounded clip for the
            fill. The fill is nested inside, so its static left end is rounded
            by THIS clip (which never resizes) rather than by the fill's own
            border-radius. That avoids a browser repaint quirk where, while the
            fill's width springs from 0, the stale left corner renders square
            for the first frames of the grow animation. */}
        <div
          ref={trackElementRef}
          style={{
            position: "absolute",
            inset: 0,
            background: TRACK_COLOR,
            borderRadius: PILL_CORNER_RADIUS,
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          {isIndeterminate && (
            <motion.div
              aria-hidden
              initial={prefersReducedMotion ? { left: "32%" } : { left: "-35%" }}
              animate={prefersReducedMotion ? { left: "32%" } : { left: "100%" }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { duration: 1.6, ease: "easeInOut", repeat: Infinity }
              }
              style={{
                position: "absolute",
                top: 0,
                width: "35%",
                height: "100%",
                background: FILL_GRADIENT,
                borderRadius: PILL_CORNER_RADIUS,
                boxShadow: `0 0 10px ${GLOW_INNER_COLOR}`,
              }}
            />
          )}
          {/* Fill — pill + overflow:hidden gives a rounded leading edge. */}
          {!isIndeterminate && (
            <motion.div
              initial={{ width: initialWidthCss }}
              animate={{ width: filledWidthCss }}
              transition={{ width: widthTransition }}
              onUpdate={(latestValues) => noteDrawnFillPercentage(getPercentageFromAnimatedWidth(latestValues.width))}
              onAnimationComplete={() => noteDrawnFillPercentage(clampedProgressPercentage)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                borderRadius: PILL_CORNER_RADIUS,
                overflow: "hidden",
                // Clip composited canvas/blend layers at the rounded cap too.
                // WebKit can otherwise paint their rectangular layer bounds.
                clipPath: `inset(0 round ${PILL_CORNER_RADIUS}px)`,
                // Isolate so the canvas blend modes blend only within the fill,
                // not against the track or the page behind the bar.
                isolation: "isolate",
              }}
            >
              {/* Base green gradient. */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: FILL_GRADIENT,
                }}
              />
              {showAnimatedTextureCanvases && trackPixelWidth > 0 ? (
                <>
                  <AuroraHueDriftCanvas
                    canvasWidthInPixels={trackPixelWidth}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                  <AuroraSmokeCanvas
                    isLargeBar={isLargeBar}
                    canvasWidthInPixels={trackPixelWidth}
                    prefersReducedMotion={prefersReducedMotion}
                  />
                </>
              ) : null}
              {/* Glossy sheen — the 3D, cylindrical-tube highlight. */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background: FILL_GLOSS_GRADIENT,
                  pointerEvents: "none",
                }}
              />
              {/* Keep the highlight inside the animated fill's rounded clip.
                  A sibling with its own width spring can outrun the fill. */}
              {shouldEmphasizeNewProgressDelta && trackPixelWidth > 0 && (
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 0,
                    // Anchor to the track as the containing fill animates.
                    left: (trackPixelWidth * (clampedNewProgressDeltaStartPercentage ?? 0)) / 100,
                    right: 0,
                    height: "100%",
                    background: NEW_PROGRESS_DELTA_FILL_GRADIENT,
                    pointerEvents: "none",
                  }}
                />
              )}
            </motion.div>
          )}
          {/* Marks — over the fill, clipped by the track like everything else,
              so the flash glows inside the bar and nothing shows outside it. */}
          {!isIndeterminate &&
            markPercentages.map((markPercentage) => {
              // Progress that falls back below a mark makes it solid again at once.
              const hasFillPassedMark =
                drawnFillPercentageForMarks >= markPercentage && clampedProgressPercentage >= markPercentage;
              return (
                <motion.div
                  key={markPercentage}
                  aria-hidden
                  data-progress-mark={hasFillPassedMark ? "passed" : "ahead"}
                  // No animation on mount: a mark already passed when the bar
                  // appears is simply faint.
                  initial={false}
                  animate={
                    hasFillPassedMark
                      ? prefersReducedMotion
                        ? { opacity: PASSED_MARK_OPACITY, boxShadow: MARK_WITHOUT_GLOW }
                        : {
                            opacity: [1, 1, PASSED_MARK_OPACITY],
                            boxShadow: [MARK_WITHOUT_GLOW, MARK_GLOW, MARK_WITHOUT_GLOW],
                          }
                      : { opacity: 1, boxShadow: MARK_WITHOUT_GLOW }
                  }
                  transition={
                    hasFillPassedMark && !prefersReducedMotion
                      ? { duration: MARK_FLASH_SECONDS, times: [0, 0.3, 1], ease: "easeOut" }
                      : { duration: 0 }
                  }
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${markPercentage}%`,
                    width: MARK_WIDTH_IN_PIXELS,
                    marginLeft: -MARK_WIDTH_IN_PIXELS / 2,
                    background: MARK_COLOR,
                    pointerEvents: "none",
                  }}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
});
